import { delay } from '@/shared/datetime/utils/time'

import {
	CLOUD_ESTATE_FIXTURE,
	COLLECTOR_ESTATE_FIXTURE,
	LAST_CHECKED_SECONDS_AGO,
	LOG_VOLUME_FIXTURE,
	NEEDS_ATTENTION_FIXTURES,
} from './fixtures/overview'

import type { IntegrationsOverview } from './types'

/**
 * How long the fixture "request" takes — see the Collectors sub-feature's
 * `api.ts` for why this exists at all. Same value, so the two surfaces feel
 * alike when you click between them.
 */
const FIXTURE_LATENCY_MS = 350

/**
 * The cross-kind health rollup behind `/integrations`.
 *
 * **This is the swap seam.** There is no overview endpoint yet; when one lands,
 * this body becomes a `get()` call and `fixtures/overview.ts` is deleted.
 * Nothing above it — query factory, components, skeletons — should have to
 * change.
 *
 * Deliberately **not** assembled from the real integrations list plus the
 * Collectors fixture: half-real numbers next to half-authored ones is the worst
 * of both, because the page would contradict itself the moment a tenant's actual
 * Cloud estate stopped matching the authored Collector estate beside it. One
 * source, one seam.
 *
 * `signal` is honoured rather than accepted-and-ignored, so a tenant switch
 * cancels this the way it will cancel the real request (P-03).
 */
export async function fetchIntegrationsOverview(
	signal?: AbortSignal,
): Promise<IntegrationsOverview> {
	await delay(FIXTURE_LATENCY_MS)
	signal?.throwIfAborted()

	const now = Date.now()
	const at = (secondsAgo: number) =>
		new Date(now - secondsAgo * 1000).toISOString()

	const { lastSyncSecondsAgo, ...cloud } = CLOUD_ESTATE_FIXTURE
	const { lastCheckInSecondsAgo, ...collectors } = COLLECTOR_ESTATE_FIXTURE

	return {
		cloud: { ...cloud, lastSyncAt: at(lastSyncSecondsAgo) },
		collectors: { ...collectors, lastCheckInAt: at(lastCheckInSecondsAgo) },
		logVolume: LOG_VOLUME_FIXTURE,
		needsAttention: NEEDS_ATTENTION_FIXTURES,
		lastCheckedAt: at(LAST_CHECKED_SECONDS_AGO),
	}
}
